import React, { useEffect } from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { withInject } from '@piwikpro/platform';
import { RouterService, useAdministrationOverlay } from '@piwikpro/router-crate';
import {
  Layout,
  Card,
  Stack,
  Text,
  Button,
  Icon,
  HelpLink,
  Tag,
  Spinner,
  withTranslation,
} from '@piwikpro/ui-components';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { OrganizationState } from '../../redux';
import { Organization } from '../../Organization';
import LogoAndFaviconEdit from '../LogoAndFaviconEdit';

const mapStateToProps = (state: {
  organization: OrganizationState
  session: SessionState
}) => ({
  isLoading: state.organization.branding.isLoading,
  failed: state.organization.branding.failed,
  isDefaultSetup: state.organization.branding.default,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface IFailMessageProps {
  t: (key: string) => string
}

interface InjectedProps {
  organizationApi: Organization
  routerService: RouterService
}

export interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

const FailMessage = withTranslation(['administration'])(({ t }: IFailMessageProps) => (
  <Stack
    vertical
    horizontalAlignment="middle"
    verticalAlignment="center"
    spacing="extraWide"
  >
    <Stack.Item fill />
    <Stack.Item>
      <Stack
        vertical
        horizontalAlignment="middle"
      >
        <Stack.Item>
          <Icon
            color="red"
            name="exclamation-mark-circle"
            size="medium"
          />
        </Stack.Item>
        <Stack.Item>
          <Text color="Red" dataId="TextStyle">
            {t('accountSecurity.twoFactorAuth.load.failed.description')}
          </Text>
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item fill />
  </Stack>
));

const Wrapper = withTranslation(['administration'])(({ t, children }: { t: TFunction, children: React.ReactNode }) => (
  <Layout dataId="LogoAndFaviconSettings">
    <Layout.AnnotatedSection
      title={t('accountSettings.logoAndFavicon.layout.title')}
    />
    <Layout.Column dominant>
      {children}
    </Layout.Column>
  </Layout>
));

export const LogoAndFavicon: React.FunctionComponent<Props> = ({
  isLoading,
  failed,
  isDefaultSetup,
  organizationApi,
  routerService,
  t,
}) => {
  const overlay = useAdministrationOverlay();
  const editOverlay = overlay === '/o/edit-logo' ? <LogoAndFaviconEdit /> : null;

  useEffect(() => {
    organizationApi.fetchBranding().catch(() => {});
  }, []);

  if (isLoading || failed) {
    return (
      <Wrapper>
        <Card>
          <Card.Section>
            {isLoading && <Spinner />}
            {failed && <FailMessage />}
          </Card.Section>
        </Card>
        {editOverlay}
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <Card>
        <Card.Section>
          <Stack vertical horizontalAlignment="middle">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('accountSettings.logoAndFavicon.layout.section.header')}</Text>
                </Stack.Item>
                <Stack.Item>
                  <Text as="p">
                    {t('accountSettings.logoAndFavicon.layout.section.text')}
                  </Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Section subdued padding="narrow">
                  <Stack verticalAlignment="center">
                    <Stack.Item fill>
                      <Stack verticalAlignment="center" spacing="narrow">
                        <Stack.Item>
                          <Text as="p" weight={600} dataId="Label">{t('accountSettings.logoAndFavicon.layout.settingsPreview.label')}</Text>
                        </Stack.Item>
                        <Stack.Item>
                          <Tag color={isDefaultSetup ? 'white' : 'green'}>
                            {t(`accountSettings.logoAndFavicon.layout.settingsPreview.settings.${isDefaultSetup ? 'default' : 'custom'}`)}
                          </Tag>
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                    {!isDefaultSetup && (
                      <Stack.Item>
                        <Button
                          appearance="simple"
                          text={t('accountSettings.logoAndFavicon.layout.settingsPreview.settings.restoreDefault')}
                          onClick={async () => {
                            try {
                              await organizationApi.resetBranding();
                              setTimeout(() => window.location.reload(), 1000);
                            // eslint-disable-next-line no-empty
                            } catch {}
                          }}
                        />
                      </Stack.Item>
                    )}
                    <Stack.Item previousSpacing="narrow">
                      <Button
                        text={t('accountSettings.logoAndFavicon.layout.settingsPreview.settings.edit')}
                        onClick={() => routerService.openAdministrationOverlay('/o/edit-logo')}
                      />
                    </Stack.Item>
                  </Stack>
                </Card.Section>
              </Card>
            </Stack.Item>
            <Stack.Item>
              <HelpLink href="https://help.piwik.pro/support/account/add-a-custom-logo-and-favicon">
                Add a custom logo and favicon
              </HelpLink>
            </Stack.Item>
          </Stack>
        </Card.Section>
      </Card>
      {editOverlay}
    </Wrapper>
  );
};

export default connector(withInject<InjectedProps>({
  organizationApi: 'OrganizationCrate.organization',
  routerService: 'RouterCrate.router',
})(withTranslation(['administration'])(LogoAndFavicon)));
